package de.oevbs.kunde.kundenportal2.navigation

import de.oevbs.kosmos.ui.icons.Icon
import de.oevbs.kunde.kundenportal2.authentication.PortalAuthority
import de.oevbs.kunde.kundenportal2.theRouter
import dev.fritz2.core.RenderContext

/**
 * Definiert die Eigenschaften einer Seite, zu der navigiert werden kann.
 *
 * Dies umfasst bspw. deren Titel, Beschreibung und Icon sowie deren [NavigationTarget] und benötigte Berechtigungen.
 *
 * Eine Page referenziert insbesondere jeweils ihren Parent, wodurch sich eine Baumstruktur mit den in der Anwendung
 * verfügbaren Seiten ergibt. Der Baum kann von unten nach oben traversiert werden, um bspw. den Pfad einer Unterseite
 * zu ermitteln.
 * Eine Reihe weiterer Informationen können aus dieser Baumstruktur dynamisch abgeleitet werden, darunter bspw. der
 * Pfad einer Unterseite.
 *
 * Die erforderlichen Berechtigungen einer Seite werden jeweils vom Parent geerbt, wenn diese nicht explizit gesetzt
 * werden. Da die Wurzelseite keine erforderlichen Berechtigungen benötigt und alle Seiten implizit auch die Wurzel
 * als Parent haben, sind alle Seiten standardmäßig für jeden Nutzer erreichbar.
 *
 * Standardmäßig wird zum Besuchen einer Seite die [PortalAuthority.Registered] benötigt.
 *
 * Die verfügbaren Seiten werden zentral im [Pages]-Objekt hinterlegt.
 *
 * @see Page.Internal
 * @see Page.External
 * @see Page.path
 */
interface Page {

    /**
     * Parent der Seite.
     */
    val parent: Page?

    /**
     * [NavigationTarget], welches zur Navigation zur aktuellen Page genutzt wird.
     */
    val target: NavigationTarget

    /**
     * Titel der Seite in der Navigation.
     */
    val linkTitle: String

    /**
     * Kurzform des Seitentitels, der bspw. in der Breadcrumb-Navigation verwendet werden kann.
     */
    val linkTitleShort: String

    /**
     * Kurzbeschreibung der Seite.
     */
    val linkDescription: String

    /**
     * Icon der Seite zur Verwendung in der Navigation oder auf Übersichtsseiten.
     */
    val icon: Icon?

    /**
     * [PortalAuthority], ab der die Seite in der Seitennavigation angezeigt wird.
     */
    val roleForMenu: PortalAuthority

    /**
     * [PortalAuthority], die benötigt wird, um den Inhalt der Seite sehen zu dürfen.
     */
    val roleForRoute: PortalAuthority

    /**
     * Gibt an, ob die Seite in der Navigation berücksichtigt werden soll.
     *
     * Ist dieser Wert `true`, erscheint die Seite in der Navigation. Ist der Wert `false`, wird die Seite dort
     * ausgeblendet und dient als rein interne Referenz.
     */
    val showInMenu: Boolean


    /**
     * Interne [Page], die einen anzeigbaren [content] hat und über eine [route] angesteuert werden kann.
     */
    interface WithContent : Page {

        /**
         * Route, unter der die Seite zu erreichen ist.
         */
        val route: String

        /**
         * Content, der als Seiteninhalt gerendert werden soll.
         */
        val content: RenderContext.(Page) -> Unit
    }


    /**
     * Stellt eine rein Kundenportal-interne Seite mit [content] dar.
     */
    data class Internal(
        override val parent: Page?,
        override val route: String,
        override val linkTitle: String,
        override val linkTitleShort: String = linkTitle,
        override val roleForMenu: PortalAuthority = parent?.roleForMenu ?: PortalAuthority.Registered,
        override val roleForRoute: PortalAuthority = parent?.roleForRoute ?: PortalAuthority.Registered,
        override val linkDescription: String = "",
        override val icon: Icon? = null,
        override val showInMenu: Boolean = true,
        override val content: RenderContext.(Page) -> Unit
    ) : WithContent {
        override val target: NavigationTarget.Internal = NavigationTarget.Internal(theRouter, route)
    }

    /**
     * Stellt eine Externe Seite dar, die unter der angegebenen [url] erreicht werden kann.
     */
    data class External(
        override val parent: Page,
        private val url: String,
        override val linkTitle: String,
        override val roleForMenu: PortalAuthority = parent.roleForMenu,
        override val roleForRoute: PortalAuthority = parent.roleForRoute,
        override val linkDescription: String = "",
        override val icon: Icon? = null,
        override val showInMenu: Boolean = true,
    ) : Page {
        override val linkTitleShort: String = linkTitle
        override val target: NavigationTarget.External = NavigationTarget.External(url)
    }

    /**
     * Dekoriert eine kundenportal-interne [Page.WithContent] so, dass eine externe URL als Fallback geöffnet wird, wenn
     * der Nutzer nicht die nötigen Berechtigungen zum _Besuchen_ der Seite hat.
     *
     * Die Berechtigungen werden mithilfe der angegebenen [hasAuthority]-Funktion evaluiert.
     *
     * @see roleForRoute
     */
    class InternalWithFallbackUrl(
        private val decorated: WithContent,
        private val hasAuthority: (PortalAuthority) -> Boolean,
        private val fallbackUrl: String
    ) : WithContent by decorated {

        override val target: NavigationTarget
            get() {
                return if (hasAuthority(decorated.roleForRoute)) decorated.target
                else NavigationTarget.External(fallbackUrl)
            }
    }
}


private tailrec fun buildPathRecursively(
    page: Page?,
    acc: List<Page> = emptyList()
): List<Page> =
    when(page) {
        null -> acc
        else -> buildPathRecursively(page = page.parent, acc = listOf(page) + acc)
    }

/**
 * Der Pfad der aktuellen Page.
 *
 * Der Pfad ist eine geordnete Liste, die als erstes Element die Baumwurzel und als letztes Element die aktuelle Page
 * beinhaltet. Die Elemente dazwischen sind die weiteren Knoten, die im beschriebenen Pfad durch die Baumstruktur
 * liegen.
 *
 * Der Pfad kann bspw. für sogenannte Breadcrumbs verwendet werden.
 *
 * Beispiel:
 * ```
 * val page1 = Page.Internal(parent = Page.Root, ...)
 * val page2 = Page.Internal(parent = page1, ...)
 * val page3 = Page.Internal(parent = page2, ...)
 *
 * println(page3.path)
 * // [Page.Root, page1, page2, page3]
 * ```
 */
val Page.path: List<Page>
    get() = buildPathRecursively(this)

/**
 * Die Parents der aktuellen Seite in hierarchischer Reihenfolge.
 *
 * Entspricht dem Pfad der Parent-Page, also:
 * ```
 * val parents = somePage.parent.path
 * val parents2 = somePage.parents
 * println(parents == parents2) // true
 * ```
 *
 * Siehe [path] für mehr Informationen.
 * ```
 */
val Page.parents: List<Page>
    get() = buildPathRecursively(parent)


/**
 * Convenience-Funktion, um die Seite zu besuchen.
 *
 * Intern wird [NavigationTarget.visit] aufgerufen.
 */
fun Page.visit(): Unit = target.visit()